import { useCallback, useRef } from 'react';
import { useEventListener } from './use-event-listener';
/**
 * Refires an event to it's original target.
 *
 * Refired events can be detected via the `event.isTrusted` property, which will be `false` for a refired event.
 */
function refireOriginalEvent(event) {
    var _a;
    (_a = event.target) === null || _a === void 0 ? void 0 : _a.dispatchEvent(event);
}
export function useDoubleClick(options) {
    const { targetRef, onDoubleClick, onSingleClick, latency = 200 } = options;
    const doubleClickDistanceThreshold = 10;
    const handleDoubleClick = useCallback((event) => {
        const result = onDoubleClick(event);
        if (result !== false) {
            refireOriginalEvent(event);
        }
    }, [onDoubleClick]);
    const handleSingleClick = useCallback((event) => {
        const result = onSingleClick === null || onSingleClick === void 0 ? void 0 : onSingleClick(event);
        if (result !== false) {
            refireOriginalEvent(event);
        }
    }, [onSingleClick]);
    const distance = (x1, y1, x2, y2) => {
        const x = Math.abs(x1 - x2);
        const y = Math.abs(y1 - y2);
        return Math.sqrt(x * x + y * y);
    };
    const lastEvent = useRef();
    const lastEventTimeout = useRef();
    const handleClick = useCallback((event) => {
        // If the click has been triggered by the timeout, allow it to propagate as normal.
        // Note: `isTrusted` will be `false` if the event was triggered programmatically.
        if (!event.isTrusted) {
            lastEvent.current = undefined;
            lastEventTimeout.current = undefined;
            return;
        }
        // Prevent the original click from propagating.
        event.preventDefault();
        event.stopPropagation();
        // Handle double-click events.
        if (lastEvent.current) {
            // Compare distance between last click and new click
            const distanceBetweenClicks = distance(lastEvent.current.x, lastEvent.current.y, event.x, event.y);
            if (distanceBetweenClicks < doubleClickDistanceThreshold) {
                // If less than threshold then treat as a double click
                window.clearTimeout(lastEventTimeout.current);
                lastEventTimeout.current = undefined;
                lastEvent.current = undefined;
                handleDoubleClick(event);
            }
            else {
                // Treat the first click as a single click
                window.clearTimeout(lastEventTimeout.current);
                lastEventTimeout.current = undefined;
                handleSingleClick(lastEvent.current);
                lastEvent.current = undefined;
                // Set a timeout for the second click
                lastEvent.current = event;
                lastEventTimeout.current = window.setTimeout(() => {
                    handleSingleClick(event);
                }, latency);
            }
        }
        // Set a timeout that repeats the original click if a double-click doesn't occur.
        else {
            lastEvent.current = event;
            lastEventTimeout.current = window.setTimeout(() => {
                handleSingleClick(event);
                lastEvent.current = undefined;
            }, latency);
        }
    }, [handleDoubleClick, handleSingleClick, latency]);
    useEventListener(targetRef, 'click', handleClick, { passive: false });
}
//# sourceMappingURL=use-double-click.js.map